import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { Button, Divider, Grid2, Stack, Typography } from "@mui/material";

import {
  IconDownload,
  IconShoppingCart,
  IconUser,
  IconWallet,
} from "@tabler/icons-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import ContainerLoading from "../components/ContainerLoading";
import { Input } from "../components/Input";
import { useMediaQuery, useTheme } from "@mui/material";
import PageHeader from "../components/PageHeader";

import {
  cancelByIdStoreOrders,
  listByIdStoreOrders,
} from "../services/products";

import {
  foarmtPaymentStatus,
  formatCpfCnpj,
  formatDate,
  formatNumber,
  formatPhone,
  formatPrice,
  unMaskMoney,
} from "../helpers/format";
import OrderStatusTag from "../components/OrderStatusTag";
import PaymentStatusTag from "../components/PaymentStatusTag";
import ModalConfirmWithButton from "../components/ModalConfirmWithButton";

import { canceledInvoice } from "../services/checkout";

import Info from "../components/Info";
import SelectComponent from "../components/SelectComponent";
import ModalFinanceDetails from "../components/ModalFinanceDetails";

const OrdersDetails = () => {
  const params = useParams();
  const { state } = useLocation();

  const [paymentType, setPaymentType] = useState("");
  const [installments, setInstallments] = useState(1);
  const [open, setOpen] = useState(false);

  const {
    data: order,
    refetch,
    isPending,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["listByIdStoreOrders", params?.id],
    queryFn: async () => await listByIdStoreOrders(params?.id),
    initialData: { orderItem: [], customer: {} },
  });

  const products = order?.orderItem?.map((i: any) => ({
    id: crypto.randomUUID(),
    idProduct: i.id,
    label: i.product?.name,
    productPrice: i.amount,
    price: i.price,
    qty: i.quantity,
  }));

  const total = order.amount;
  const discount = order.discount;

  useEffect(() => {
    setPaymentType(order?.paymentType);
    setInstallments(Number(order?.installments));
  }, [order]);

  const paymentPaid =
    order?.payments?.find(
      (p: any) => p.status === "PAID" || p.status === "CONFIRMED"
    ) || null;

  const formatPaymentOrder = () => {
    if (paymentType === "CREDIT_CARD") {
      return `Cartão de crédito em ${installments}x de ${formatPrice(
        total / Number(installments)
      )}`;
    }

    if (paymentType === "PIX_INSTALLMENTS") {
      return `PIX Parcelado  em ${installments}x de ${formatPrice(
        total / installments
      )}`;
    }

    return "PIX";
  };

  const exportReceipt = async () => {
    const element = document.getElementById("receipt-content");
    if (!element) return;

    const canvas = await html2canvas(element);
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

    // Adicionar rodapé
    const pageHeight = pdf.internal.pageSize.getHeight();
    pdf.setFontSize(10);
    pdf.text(
      "Obrigado por comprar com a Fight GO - www.fightgo.com.br",
      pdfWidth / 2,
      pageHeight - 10,
      { align: "center" }
    );

    pdf.save(`comprovante_pedido_${params?.id}.pdf`);
  };

  useEffect(() => {
    if (order?.payment?.bankPixCode && state?.showPix) {
      setOpen(true);
    }
    if (order?.payment?.webToken && state?.showCreditCard) {
      setOpen(true);
    }
  }, [order]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <ContainerLoading loading={isPending || isLoading || isFetching}>
      {open && (
        <ModalFinanceDetails
          data={order.payment}
          showCreditCard={state?.showCreditCard}
          showPix={state?.showPix}
          open={open}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}

      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Detalhes do pedido"
          subtitle="Detalhes do seu pedido."
          sticky
          options={
            <Stack
              flexDirection={"row"}
              justifyContent={"flex-end"}
              alignItems={"center"}
              gap="1rem"
            >
              {order.status !== "CANCELED" && (
                <ModalConfirmWithButton
                  title="Cancelar pedido"
                  subtitle={
                    <>
                      Tem certeza que deseja cancelar esse pedido? <br />
                      <br />- Os produtos retornam ao estoque.
                      <br />- Cancelamos os pagamentos em aberto.
                    </>
                  }
                  onAction={() => {
                    refetch();
                  }}
                  buntonTitle="Cancelar pedido"
                  mutationFn={async () =>
                    await cancelByIdStoreOrders(params?.id)
                  }
                />
              )}

              <Button
                variant="contained"
                sx={{ fontSize: { xs: "12px", md: "14px" } }}
                onClick={exportReceipt}
                endIcon={<IconDownload />}
              >
                Baixar comprovante
              </Button>
            </Stack>
          }
          back
          backRouter="/orders"
        />

        <Grid2 size={12} container>
          <Grid2 size={12} container mb={2}>
            <Info
              size={{ xs: 12, md: 4 }}
              title="Status"
              subTitle={<OrderStatusTag status={order?.status} />}
            />
            <Info
              size={{ xs: 12, md: 4 }}
              title="Data da compra"
              subTitle={formatDate(order?.buyDate)}
            />
            {paymentPaid && (
              <Info
                size={{ xs: 12, md: 4 }}
                title="Pago no"
                subTitle={formatPaymentOrder()}
              />
            )}
            {order?.canceledReason && (
              <Info
                size={{ xs: 12, md: 4 }}
                title="Motivo cancelamento"
                subTitle={order?.canceledReason}
              />
            )}
          </Grid2>
          <Grid2 size={12}>
            <Typography variant="subtitle1" color="secondary.contrastText">
              DADOS DO CLIENTE
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Stack
              flexDirection={"row"}
              justifyContent={"center"}
              alignItems={"flex-end"}
              spacing={2}
              gap={1}
            >
              {order?.customer?.name && (
                <SelectComponent
                  label="Cliente"
                  placeholder="Digite o nome do seu cliente ou telefone "
                  value={order?.customer?.id}
                  disabled
                  startIcon={<IconUser />}
                  options={[
                    {
                      value: order?.customer?.id,
                      label: `${order?.customer?.name} - ${formatCpfCnpj(
                        order?.customer?.document
                      )} - ${formatPhone(order?.customer?.phone)}`,
                    },
                  ]}
                />
              )}
            </Stack>
          </Grid2>
          <Grid2 size={12} marginY={1}>
            <Divider />
          </Grid2>{" "}
          <Grid2 size={12}>
            <Typography variant="subtitle1" color="secondary.contrastText">
              ITENS DO PEDIDO
            </Typography>{" "}
          </Grid2>
          <Grid2
            size={12}
            sx={{
              backgroundColor: "#282828",
              padding: "1rem",
              borderRadius: "1rem",
            }}
          >
            {products.map((item: any) => (
              <Grid2
                container
                key={item.id}
                direction="row"
                alignItems="center"
                spacing={1}
                mt={1}
              >
                <Grid2 size={{ xs: 12, md: 5 }}>
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="subtitle2" textTransform="uppercase">
                      Produto
                    </Typography>
                    <SelectComponent
                      name="productId"
                      placeholder="Digite o nome ou SKU do seu produto"
                      value={item.idProduct}
                      startIcon={<IconShoppingCart />}
                      options={products?.map((i: any) => ({
                        value: i.idProduct,
                        label: i.label,
                      }))}
                      disabled
                    />
                  </Stack>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 2 }}>
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="subtitle2" textTransform="uppercase">
                      Quantidade
                    </Typography>
                    <Input
                      value={formatNumber(item.qty)}
                      disabled
                      size="small"
                    />
                  </Stack>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 2 }}>
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="subtitle2" textTransform="uppercase">
                      Valor unitário
                    </Typography>
                    <Input
                      value={formatPrice(item.price)}
                      disabled
                      size="small"
                    />
                  </Stack>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 2 }}>
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="subtitle2" textTransform="uppercase">
                      Total
                    </Typography>
                    <Input
                      disabled
                      value={formatPrice(
                        (
                          Number(item.qty) * Number(unMaskMoney(item.price))
                        ).toFixed(2)
                      )}
                      size="small"
                    />
                  </Stack>
                </Grid2>
              </Grid2>
            ))}
          </Grid2>
          <Grid2
            size={12}
            direction="row"
            alignItems="center"
            sx={{
              backgroundColor: "#282828",
              padding: "1rem",
              borderRadius: "1rem",
            }}
          >
            <Stack flexDirection={"row"} justifyContent={"space-between"}>
              <Typography variant="subtitle1" fontWeight={700}>
                Desconto
              </Typography>
              <Typography variant="h6" color="error" fontWeight={700}>
                {formatPrice(discount < 0 ? discount : 0)}
              </Typography>
            </Stack>
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              mt="10px"
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Total
              </Typography>
              <Typography variant="h6" color="success" fontWeight={700}>
                {formatPrice(total)}
              </Typography>
            </Stack>
          </Grid2>
          <Grid2 size={12} marginY={2}>
            <Divider />
          </Grid2>
          <Grid2 size={12}>
            <Typography variant="subtitle1" color="secondary.contrastText">
              PAGAMENTO
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <SelectComponent
              label="Tipo de pagamento"
              placeholder="Escolha o tipo de pagamento"
              value={order?.paymentType}
              disabled
              startIcon={<IconWallet />}
              options={[
                { value: "MONEY", label: "Dinheiro" },
                { value: "PIX_BILLET", label: "PIX à vista" },
                {
                  value: "PIX_INSTALLMENTS",
                  label: `PIX Parcelado ${installments || 1}X`,
                },
                {
                  value: "CREDIT_CARD",
                  label: `Cartão de crédito ${installments || 1}X`,
                },
              ]}
            />
          </Grid2>
          <Grid2 container spacing={1} size={12}>
            {order?.payments?.map((i: any) => (
              <Grid2 size={12}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent={"space-between"}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  sx={{
                    backgroundColor: "#282828",
                    padding: "1rem",
                    borderRadius: "1rem",
                    gap: isMobile ? "10px" : "0px",
                  }}
                >
                  <Stack width="120px">
                    <PaymentStatusTag status={i.status} />
                  </Stack>
                  {!i.paymentDate && (
                    <Info
                      size="auto"
                      title="Vencimento"
                      subTitle={formatDate(i.dueDate)}
                    />
                  )}

                  {i.paymentDate && (
                    <Info
                      size="auto"
                      title="Pago em"
                      subTitle={formatDate(i.paymentDate)}
                    />
                  )}
                  <Info
                    size="auto"
                    title="Valor"
                    subTitle={formatPrice(i.value)}
                  />

                  {!["CANCELED"].includes(i.status) && (
                    <Grid2 size="auto">
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        gap={1}
                        width={"310px"}
                        justifyContent={"flex-end"}
                      >
                        {![
                          "CANCELED",
                          "PAID",
                          "CONFIRMED",
                          "CONFIRMED_INSTALLMENT",
                        ].includes(i.status) && (
                          <ModalConfirmWithButton
                            title="Cancelar fatura"
                            subtitle={
                              "Tem certeza que deseja cancelar essa fatura?"
                            }
                            onAction={() => {
                              refetch();
                            }}
                            buntonTitle="Cancelar fatura"
                            mutationFn={async () =>
                              await canceledInvoice(i?.id)
                            }
                          />
                        )}
                        {!["CANCELED"].includes(i.status) && (
                          <Button
                            variant="contained"
                            endIcon={<IconWallet />}
                            onClick={() => {
                              window.open(`/i/${i?.webToken}`, "_blank");
                            }}
                          >
                            Ver fatura
                          </Button>
                        )}
                      </Stack>
                    </Grid2>
                  )}
                </Stack>
              </Grid2>
            ))}
          </Grid2>
          <Grid2 size={12} marginY={2}>
            <Divider />
          </Grid2>
          <Grid2 size={12}>
            <Typography variant="subtitle1" color="secondary.contrastText">
              INFORMAÇÂO ADCIONAL ( Opcional )
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Input multiline rows={5} disabled value={order?.adcionalInfo} />
          </Grid2>
        </Grid2>
      </Grid2>

      <div
        id="receipt-content"
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          padding: 20,
          fontFamily: "Arial, sans-serif",
          backgroundColor: "#fff",
          color: "#000",
        }}
      >
        <Grid2 size={12} container spacing={2} mb={2}>
          <Grid2 size={12}>
            <Typography fontSize={"20px"} textAlign={"center"} fontWeight={700}>
              Comprovante de Pedido #{order.id}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 12 }} borderRadius={2}>
            <Stack
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              alignItems={"center"}
            >
              <Stack>
                <Typography fontWeight={700} variant="subtitle1" fontSize={18}>
                  {order?.store?.name}
                </Typography>
                <Typography>CEP: {order?.store?.address?.cep}</Typography>
                <Typography>
                  {order?.store?.address?.city} - {order?.store?.address?.state}{" "}
                </Typography>
              </Stack>
            </Stack>
          </Grid2>{" "}
          <Grid2 size={12}>
            <Stack
              flexDirection={"column"}
              sx={{ borderBottom: "solid 1px black" }}
            />
          </Grid2>
          <Info size={4} title="Cliente" subTitle={order?.customer?.name} />
          <Info
            size={4}
            title="Documento"
            subTitle={formatCpfCnpj(order?.customer?.document || "")}
          />
          <Info
            size={4}
            title="Data da compra"
            subTitle={formatDate(order?.buyDate)}
          />
          <Info
            size={4}
            title="Status"
            subTitle={foarmtPaymentStatus(order?.status)}
          />
          {paymentPaid && (
            <Info size={4} title="Pago no" subTitle={formatPaymentOrder()} />
          )}
          {order?.canceledReason && (
            <Info
              size={4}
              title="Motivo cancelamento"
              subTitle={order?.canceledReason}
            />
          )}
          <Info size={4} title="Total" subTitle={formatPrice(total)} />
          <Grid2 size={12}>
            <Stack
              flexDirection={"column"}
              sx={{ borderBottom: "solid 1px black" }}
            />
          </Grid2>
        </Grid2>

        <table
          style={{ width: "100%", marginTop: 20, borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Produto</th>
              <th style={{ textAlign: "left" }}>Qtd</th>
              <th style={{ textAlign: "left" }}>Unitário</th>
              <th style={{ textAlign: "left" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {products.map((item: any, i: number) => (
              <tr key={i}>
                <td style={{ textAlign: "left" }}>{item.label}</td>
                <td style={{ textAlign: "left" }}>{item.qty}</td>
                <td style={{ textAlign: "left" }}>{formatPrice(item.price)}</td>
                <td style={{ textAlign: "left" }}>
                  {formatPrice(
                    Number(item.qty) * Number(unMaskMoney(item.price))
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ContainerLoading>
  );
};

export default OrdersDetails;
